import { evaluate } from '../core';
import { usePermissionsSnapshot } from './usePermissionsSnapshot';

import type { Action, PermissionDecision, PermissionsRetry, ResourceFacts } from '../core';

/** How several actions combine. AND by default - see `Asked`. */
export type CanMode = 'all' | 'any';

/**
 * What a gate gets back: the decision, plus how to ask again.
 *
 * `retry` is deliberately NOT on `PermissionDecision`. That type is `evaluate`'s pure, per-action
 * output - it is combined, compared and held in memo dependencies - and a React-bound callback has
 * no place in it. The retry is snapshot-level anyway: there is one source to re-ask, not one per
 * action, so threading it through `evaluate` would carry it past every rule that cannot use it.
 */
export type PermissionAnswer = PermissionDecision & PermissionsRetry;

/** A decision that can no longer change: it neither waits for the snapshot nor inherits its failure. */
const isSettled = (decision: PermissionDecision) => !decision.isLoading && !decision.isError;

/**
 * A settled answer wins over a pending one whenever it settles the whole question: one yes settles
 * `any`, one determined no settles `all`. Without that, asking about two actions where one is
 * already decided would show a spinner for an answer that cannot change - the flicker `evaluate`
 * avoids per action, avoided again across actions.
 */
const combine = (decisions: PermissionDecision[], mode: CanMode): PermissionDecision => {
  // An empty list denies. Vacuous truth would open the gate instead.
  if (decisions.length === 0) return { allowed: false, isLoading: false, isError: false };

  const settlesIt =
    mode === 'any'
      ? decisions.some((decision) => decision.allowed)
      : decisions.some((decision) => isSettled(decision) && !decision.allowed);

  if (settlesIt) return { allowed: mode === 'any', isLoading: false, isError: false };
  // Pending before unanswerable. `evaluate` mirrors this order per action - change both together.
  if (decisions.some((decision) => decision.isLoading)) return { allowed: false, isLoading: true, isError: false };
  if (decisions.some((decision) => decision.isError)) return { allowed: false, isLoading: false, isError: true };

  return { allowed: mode === 'all', isLoading: false, isError: false };
};

/**
 * The single entry point for "may I do this?".
 *
 * ```ts
 * const { allowed, isLoading } = useCan('natter:manage', { isOwned, hosts, communityId });
 * ```
 *
 * Answers about the scope in context - the active community unless a `PermissionsProvider` says
 * otherwise. `facts` only ever refine that answer, per action, per the `FACT_RULES` table in
 * `evaluate`: most actions ignore them entirely, so passing none asks the plain question.
 *
 * Fails closed. `isError` is reported rather than swallowed so a caller CAN tell "unreachable" from
 * "no", but `allowed` is false either way, which is what every gate in the app does today.
 *
 * Not memoized, deliberately: call sites pass their action list and facts inline, so a memo keyed on
 * those would miss on every render while still costing a comparison - and it would imply a reference
 * stability this cannot offer. The expensive half is already memoized where it belongs; the
 * adapter's `allowed` Set is stable and `evaluate` is a couple of Set lookups. Destructure the
 * result rather than putting the object in a dependency array.
 */
export const useCan = (actions: Action | Action[], facts?: ResourceFacts, mode: CanMode = 'all'): PermissionAnswer => {
  const snapshot = usePermissionsSnapshot();
  const asked = Array.isArray(actions) ? actions : [actions];

  const decision = combine(
    asked.map((action) => evaluate(snapshot, action, facts)),
    mode
  );

  // Passed straight through from the snapshot. A caller that ignores it behaves exactly as before,
  // which is what keeps the twenty-one existing call sites unchanged.
  return { ...decision, retry: snapshot.retry, canRetry: snapshot.canRetry };
};
